import { UseFormReturn } from 'react-hook-form';
import { Button } from '@theraply/ui';
import {
  PencilIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  RegistrationFormData,
  ReviewData
} from '../../../hooks/useRegistrationWizard';

interface ReviewStepProps {
  form: UseFormReturn<ReviewData>;
  formData: RegistrationFormData;
  onEditStep: (step: number) => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

interface SectionProps {
  title: string;
  stepNumber: number;
  onEdit: () => void;
  children: React.ReactNode;
  status?: 'complete' | 'warning';
}

function ReviewSection({ title, stepNumber, onEdit, children, status = 'complete' }: SectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {status === 'complete' ? (
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-primary-600 hover:text-primary-700"
        >
          <PencilIcon className="h-4 w-4 mr-1.5" />
          Edit
        </Button>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string | string[] | null | undefined;
  isArray?: boolean;
}

function InfoRow({ label, value, isArray = false }: InfoRowProps) {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 col-span-2">
        {isArray && Array.isArray(value) ? (
          <div className="flex flex-wrap gap-2">
            {value.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
              >
                {item}
              </span>
            ))}
          </div>
        ) : (
          <span>{value as string}</span>
        )}
      </dd>
    </div>
  );
}

export function ReviewStep({ form, formData, onEditStep, onSubmit, isSubmitting }: ReviewStepProps) {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  const { basicInfo, professionalInfo, documentUpload, practiceDetails } = formData;

  const onFormSubmit = async () => {
    await handleSubmit(async () => {
      await onSubmit();
    })();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review Your Application</h2>
        <p className="mt-2 text-sm text-gray-600">
          Please review all information carefully before submitting. You can edit any section by
          clicking the "Edit" button.
        </p>
      </div>

      <div className="space-y-6">
        <ReviewSection title="Basic Information" stepNumber={0} onEdit={() => onEditStep(0)}>
          <InfoRow label="Name" value={`${basicInfo.firstName} ${basicInfo.lastName}`} />
          <InfoRow label="Email" value={basicInfo.email} />
          <InfoRow label="Phone" value={basicInfo.phone} />
        </ReviewSection>

        <ReviewSection title="Professional Information" stepNumber={1} onEdit={() => onEditStep(1)}>
          <InfoRow label="License Type" value={professionalInfo.licenseType} />
          <InfoRow label="State" value={professionalInfo.state} />
          <InfoRow label="License Number" value={professionalInfo.licenseNumber} />
          <InfoRow label="Expiration Date" value={professionalInfo.licenseExpiration} />
          {professionalInfo.npiNumber && (
            <InfoRow label="NPI Number" value={professionalInfo.npiNumber} />
          )}
        </ReviewSection>

        <ReviewSection title="Documents" stepNumber={2} onEdit={() => onEditStep(2)}>
          <div className="space-y-3">
            {documentUpload.governmentId && (
              <div className="flex items-center space-x-3 text-sm">
                <DocumentIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">Government ID</p>
                  <p className="text-gray-500">{documentUpload.governmentId.name}</p>
                </div>
              </div>
            )}
            {documentUpload.professionalLicense && (
              <div className="flex items-center space-x-3 text-sm">
                <DocumentIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">Professional License</p>
                  <p className="text-gray-500">{documentUpload.professionalLicense.name}</p>
                </div>
              </div>
            )}
            {documentUpload.insuranceCertificate && (
              <div className="flex items-center space-x-3 text-sm">
                <DocumentIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">Insurance Certificate</p>
                  <p className="text-gray-500">{documentUpload.insuranceCertificate.name}</p>
                </div>
              </div>
            )}
          </div>
        </ReviewSection>

        <ReviewSection title="Practice Details" stepNumber={3} onEdit={() => onEditStep(3)}>
          <InfoRow label="Specialties" value={practiceDetails.specialties} isArray />
          <InfoRow label="Treatment Approaches" value={practiceDetails.treatmentApproaches} isArray />
          <InfoRow label="Session Rate" value={`$${practiceDetails.sessionRate}/hour`} />
          <InfoRow label="Session Types" value={practiceDetails.sessionTypes} isArray />
          {practiceDetails.officeAddress && (
            <InfoRow label="Office Address" value={practiceDetails.officeAddress} />
          )}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-500 mb-2">Professional Bio</p>
            <p className="text-sm text-gray-900">{practiceDetails.bio}</p>
          </div>
        </ReviewSection>
      </div>

      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
        <div className="flex items-start space-x-3">
          <input
            {...register('acceptTerms')}
            type="checkbox"
            id="acceptTerms"
            className={clsx(
              'mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500',
              errors.acceptTerms && 'border-red-500'
            )}
          />
          <div className="flex-1">
            <label htmlFor="acceptTerms" className="text-sm font-medium text-gray-900 cursor-pointer">
              I accept the Terms of Service and Privacy Policy
            </label>
            <p className="mt-1 text-sm text-gray-600">
              By checking this box, you agree to our{' '}
              <a href="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </a>
              . You also acknowledge that all information provided is accurate and truthful.
            </p>
            {errors.acceptTerms && (
              <p className="mt-2 text-sm text-red-600">{errors.acceptTerms.message}</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">What happens next?</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Our compliance team will review your application within 2-3 business days</li>
                <li>We will verify your credentials and uploaded documents</li>
                <li>You'll receive an email notification once your application is approved</li>
                <li>After approval, you can start accepting clients on our platform</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-6 border-t border-gray-200">
        <Button
          type="button"
          onClick={onFormSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
        </Button>
      </div>
    </div>
  );
}
